import { useState } from "react";
import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";

const problems = [
  {
    title: "Money leaves without a coordinated plan",
    visible: "Payments, interest, taxes, and expenses quietly shape the family’s future.",
    expanded: "A coordinated system helps reveal where money is flowing, what is leaving permanently, and where better habits or structure may create more control.",
  },
  {
    title: "Capital is not always available when needed",
    visible: "Money may be idle, locked away, or committed somewhere that limits flexibility.",
    expanded: "Liquidity matters because life rarely waits for the perfect timing. Families need to know which dollars are accessible and what tradeoffs come with using them.",
  },
  {
    title: "Products are chosen before their job is defined",
    visible: "A financial tool only makes sense after the problem it needs to solve is clear.",
    expanded: "When the job is defined first, tools can be judged by purpose, fit, cost, terms, and how well they support the larger plan.",
  },
  {
    title: "The next generation rarely learns the system",
    visible: "A lasting legacy includes understanding, values, and better decision-making, not only money.",
    expanded: "Families can pass down more than assets by teaching how decisions are made, why protection matters, and how stewardship works over time.",
  },
];

export const ProblemSection = () => {
  const [open, setOpen] = useState(null);

  return (
    <section data-testid="problem-section" className="section-lr bg-ivory pb-14 md:pb-16">
      <div className="container-lr grid gap-12 lg:grid-cols-[0.82fr_1.18fr] lg:gap-16">
        <div>
          <div className="flex items-center gap-4"><span className="gold-rule" /><p className="eyebrow">The Core Problem</p></div>
          <h2 className="section-heading mt-6 text-legacy">You are working harder than ever. Why does your money still feel <span className="accent-word">disconnected</span>?</h2>
        </div>
        <div className="lg:pt-2">
          <p className="text-base leading-relaxed text-slate md:text-lg">Income comes in. Bills go out. One account saves. Another invests. Another pays debt. Most families were never shown how those pieces are supposed to work together.</p>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 items-stretch">
            {problems.map((item, index) => {
              const isOpen = open === index;
              const panelId = `problem-panel-${index}`;
              return (
                <motion.article
                  key={item.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: index * 0.06 }}
                  className="group h-full rounded-card border border-taupe/50 bg-white outline-none transition-all duration-300 hover:-translate-y-1 hover:border-gold hover:bg-legacy hover:shadow-[0_22px_48px_-28px_rgba(17,17,17,0.6)] focus-within:-translate-y-1 focus-within:border-gold focus-within:bg-legacy focus-within:shadow-[0_22px_48px_-28px_rgba(17,17,17,0.6)]"
                >
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => setOpen(isOpen ? null : index)}
                    data-testid={`problem-card-${index}`}
                    className="focus-ring flex w-full flex-col p-6 text-left"
                  >
                    <span className="font-serif text-xl text-gold">0{index + 1}</span>
                    <span className="mt-3 font-serif text-xl leading-tight text-legacy transition-colors duration-300 group-hover:text-ivory group-focus-within:text-ivory">{item.title}</span>
                    <span className="mt-5 inline-flex items-center justify-between gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-bronze transition-colors duration-300 group-hover:text-gold group-focus-within:text-gold">
                      {isOpen ? "Close Details" : "Click to Learn More"}
                      <ChevronDown size={15} className={`shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
                    </span>
                  </button>
                  <div id={panelId} className={`accordion-panel ${isOpen ? "is-open" : ""}`}>
                    <div>
                      <div className="px-6 pb-6">
                        <div className="border-t border-gold/25 pt-4 space-y-3 text-sm leading-relaxed text-slate transition-colors duration-300 group-hover:text-taupe group-focus-within:text-taupe">
                          <p>{item.visible}</p>
                          <p>{item.expanded}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.article>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
